import React, { useState, useRef, useEffect } from "react";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { UI_VARIABLES } from "./constants";
import "./popover-with-arrow.scss";

interface PopoverWithArrowProps {
  icon: React.ReactNode; // Required: Icon component
  iconColor?: string; // Custom icon color
  htmlContent: string; // HTML content inside popover
  placement?: "top" | "bottom" | "left" | "right";
  popoverBgColor?: string; // Popover background color
  hideButton?: boolean;
  buttonText?: string;
}

const PopoverWithArrow: React.FC<PopoverWithArrowProps> = ({
  icon,
  iconColor = UI_VARIABLES.POPOVERBGCOLOR,
  htmlContent,
  placement = "bottom",
  popoverBgColor = UI_VARIABLES.POPOVERBGCOLOR,
  hideButton = true,
  buttonText = "",
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (popperRef.current && !popperRef.current.contains(event.target as Node)) {
      setAnchorEl(null);
    }
  };

  useEffect(() => {
    if (anchorEl) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorEl]);

  const open = Boolean(anchorEl);
  const id = open ? "dynamic-popper" : undefined;

  return (
    <div className="popper-wrapper">
      {/* Clickable Icon */}
      <IconButton
        tabIndex={-1}
        aria-describedby={id}
        onClick={handleClick}
        sx={{ color: iconColor }} // Apply custom icon color
      >
        {icon}
      </IconButton>

      {/* Popover */}
      <Popper id={id} open={open} anchorEl={anchorEl} placement={placement} ref={popperRef}>
        <Paper
          sx={{
            position: "relative",
            p: 2,
            bgcolor: popoverBgColor, // Dynamic background color
            boxShadow: 3,
          }}
        >
          {/* Arrow */}
          <div className="arrow" />
          {/* Render HTML content */}
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} tabIndex={-1} aria-hidden="true" />
          <div tabIndex={-1} aria-hidden="true">
            {!hideButton && (
              <button
                onClick={() => setAnchorEl(null)}
                tabIndex={-1}
                aria-hidden="true"
                className="button"
              >
                {buttonText}
              </button>
            )}
          </div>
        </Paper>
      </Popper>
    </div>
  );
};

export default PopoverWithArrow;
